import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
import { SecondaryBase } from './../Commons/SecondaryBase'

import { LyricsGenerator } from './Karaoke/LyricsGenerator'
import { KaraokeSongCreator } from './Karaoke/KaraokeSongCreator'

import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
import { ColorSettingsModal } from './Karaoke/ColorSettingsModal'

// NUEVO: Importa la función de tu BL para el Overlay (Asegúrate de que la ruta coincida)
import { UpdateKaraokeCamOverlay } from './../../../utilities/BL/Admin/Games/Karaoke'

/**
 * ==============================================================================
 * HELPERS (Cálculos de tiempo)
 * ==============================================================================
 */
const getBlockDurationMs = (block) => {
  if (!block) return 0;
  if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0);
  return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0);
};

const getBlockDisplayText = (block) => {
  if (!block) return "";
  if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : "";
  if (block.mode === 'uniform') return block.text || "";
  return (block.parts || []).map(p => p.text || "").join("");
};

// Valores por defecto para colores
// const DEFAULT_COLORS = { base: '#ffffff', fill: '#ffd400', stroke: '#000000' };
const DEFAULT_COLORS = { base: '#ffffff', fill: '#dc2626', stroke: '#000000' };

/**
 * ==============================================================================
 * COMPONENTE: KARAOKE DASHBOARD
 * ==============================================================================
 */
const Karaoke = ({ getOutOfTheDynamic }) => {
  const navigate = useNavigate();
  const { showMessage } = useFloatMessage();

  // --------------------------------------------------------------------------------
  // 1. ESTADOS DE DATOS
  // --------------------------------------------------------------------------------
  const [songs, setSongs] = useState([]);               
  const [selectedSongId, setSelectedSongId] = useState(""); 

  // --------------------------------------------------------------------------------
  // 2. ESTADOS DEL REPRODUCTOR
  // --------------------------------------------------------------------------------
  const [isPlaying, setIsPlaying] = useState(false);        
  const [activeBlockIndex, setActiveBlockIndex] = useState(0); 
  const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0); 

  // --------------------------------------------------------------------------------
  // 3. ESTADOS DE CONFIGURACIÓN Y UI
  // --------------------------------------------------------------------------------
  const [isColorModalOpen, setIsColorModalOpen] = useState(false); 
  
  // Estado de los botones toggle
  const [isFillingEnabled, setIsFillingEnabled] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  const [colorSettings, setColorSettings] = useState(DEFAULT_COLORS);

  // --------------------------------------------------------------------------------
  // NUEVO 3.1: ESTADOS DE LA KARAOKE CAM (OVERLAY)
  // --------------------------------------------------------------------------------
  // Visibilidad
  // const [showMic, setShowMic] = useState(false);
  // const [showNotes, setShowNotes] = useState(false);
  // const [showText, setShowText] = useState(false);

  // Posiciones (Para resolución 1920x1080)
  // const [micX, setMicX] = useState(500);
  // const [micY, setMicY] = useState(100);
  // const [micRotation, setMicRotation] = useState(0);

  const [textNotesX, setTextNotesX] = useState(800);
  const [textNotesY, setTextNotesY] = useState(800);

  //const [isCamSyncing, setIsCamSyncing] = useState(false);

  // Físicas Trackpad
  // const [isDraggingMic, setIsDraggingMic] = useState(false);
  // const dragMicAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  // const [isDraggingText, setIsDraggingText] = useState(false);
  // const dragTextAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const MAX_X_MIC = 1700;
  const MAX_Y_MIC = 1000;
  const MAX_X_TEXT = 1500;
  const MAX_Y_TEXT = 1000;

  // --------------------------------------------------------------------------------
  // 4. REFERENCIAS
  // --------------------------------------------------------------------------------
  const rafIdRef = useRef(null);       
  const lastFrameTimeRef = useRef(0);  
  const lyricsListRef = useRef(null);  

  // --------------------------------------------------------------------------------
  // 5. COMPUTADOS
  // --------------------------------------------------------------------------------
  const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId]);
  const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong]);
  const activeBlock = blocks[activeBlockIndex] || null;
  const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock]);

  // Progreso visual local (Preview)
  const fillProgress = useMemo(() => {
    if (!isFillingEnabled) return 0; 
    const total = getBlockDurationMs(activeBlock);
    if (total <= 0) return 0;
    if (activeBlock?.type === 'instrumental') return 0; 
    return Math.min(Math.max(elapsedInBlockMs, 0), total) / total;
  }, [activeBlock, elapsedInBlockMs, isFillingEnabled]);

  // Leyenda de estatus
  const statusLegend = useMemo(() => {
    if (!selectedSongId) return "Sin canción seleccionada";
    if (isPlaying) return `En reproducción - Línea #${activeBlockIndex + 1}`;
    if (activeBlockIndex > 0 && activeBlockIndex < blocks.length) return `Pausado - Línea #${activeBlockIndex + 1}`;
    if (activeBlockIndex === 0 && !isPlaying) return "Detenido (Inicio)";
    return "Listo";
  }, [selectedSongId, isPlaying, activeBlockIndex, blocks.length]);




  // Visibilidad
  const [showMic, setShowMic] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showText, setShowText] = useState(false);

  // Estados Independientes
  const [micX, setMicX] = useState(500);
  const [micY, setMicY] = useState(100);
  const [micRotation, setMicRotation] = useState(0);
  const [micScale, setMicScale] = useState(1.0);

  const [notesX, setNotesX] = useState(800);
  const [notesY, setNotesY] = useState(600);
  const [notesScale, setNotesScale] = useState(1.0);

  const [textX, setTextX] = useState(300);
  const [textY, setTextY] = useState(200);
  const [textScale, setTextScale] = useState(1.0);
  const [customText, setCustomText] = useState("¡Canta con nosotros!");

  const [isCamSyncing, setIsCamSyncing] = useState(false);

  // Físicas Trackpads
  const [isDraggingMic, setIsDraggingMic] = useState(false);
  const dragMicAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const [isDraggingNotes, setIsDraggingNotes] = useState(false);
  const dragNotesAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const [isDraggingText, setIsDraggingText] = useState(false);
  const dragTextAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  // Topes
  const MAX_X = 1700;
  const MAX_Y = 1000;


  // --------------------------------------------------------------------------------
  // 6. CICLO DE VIDA: CARGA INICIAL Y KARAOKE CAM SYNC
  // --------------------------------------------------------------------------------
  
  useEffect(() => {
    const loadSongs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs`);
            if (response.ok) {
                const data = await response.json();
                setSongs(data.songs || []);
            }
        } catch (error) { console.error("Error cargando canciones", error); }
    };
    loadSongs();
  }, []);

  // Resetear player al cambiar canción
  useEffect(() => {
    stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSongId]);

  // NUEVO: Sincronización en vivo del Overlay de Cam (Debounce)
  // useEffect(() => {
  //     setIsCamSyncing(true);
  //     const debounceTimer = setTimeout(async () => {
  //         const configPayload = {
  //             showMic, showNotes, showText,
  //             micX, micY, micRotation,
  //             textNotesX, textNotesY
  //         };
  //         try {
  //             await UpdateKaraokeCamOverlay(configPayload);
  //             setIsCamSyncing(false);
  //         } catch (e) {
  //             console.error(e);
  //             setIsCamSyncing(false);
  //         }
  //     }, 150);
  //     return () => clearTimeout(debounceTimer);
  // }, [showMic, showNotes, showText, micX, micY, micRotation, textNotesX, textNotesY]);

  useEffect(() => {
      setIsCamSyncing(true);
      const debounceTimer = setTimeout(async () => {
          const configPayload = {
              showMic, showNotes, showText,
              micX, micY, micRotation, micScale,
              notesX, notesY, notesScale,
              textX, textY, textScale, customText
          };
          try {
              await UpdateKaraokeCamOverlay(configPayload);
              setIsCamSyncing(false);
          } catch (e) {
              console.error(e);
              setIsCamSyncing(false);
          }
      }, 150);
      return () => clearTimeout(debounceTimer);
  }, [showMic, showNotes, showText, micX, micY, micRotation, micScale, notesX, notesY, notesScale, textX, textY, textScale, customText]);


  // --------------------------------------------------------------------------------
  // NUEVO 6.1 MÉTODOS DE TRACKPAD (MIC Y TEXTO)
  // --------------------------------------------------------------------------------

  // // --- Trackpad MICRÓFONO ---
  // const handleMicPointerDown = (e) => {
  //     setIsDraggingMic(true);
  //     dragMicAnchor.current = {
  //         x: e.clientX || (e.touches && e.touches[0].clientX),
  //         y: e.clientY || (e.touches && e.touches[0].clientY),
  //         startX: micX,
  //         startY: micY
  //     };
  //     if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
  // };
  // const handleMicPointerMove = (e) => {
  //     if (!isDraggingMic) return;
  //     const curX = e.clientX || (e.touches && e.touches[0].clientX);
  //     const curY = e.clientY || (e.touches && e.touches[0].clientY);
  //     const deltaX = curX - dragMicAnchor.current.x;
  //     const deltaY = curY - dragMicAnchor.current.y;
      
  //     let newX = Math.round(dragMicAnchor.current.startX - (deltaX * 3));
  //     let newY = Math.round(dragMicAnchor.current.startY - (deltaY * 3));
      
  //     setMicX(Math.max(-200, Math.min(MAX_X_MIC, newX)));
  //     setMicY(Math.max(-200, Math.min(MAX_Y_MIC, newY)));
  // };
  // const handleMicPointerUp = () => setIsDraggingMic(false);

  // // --- Trackpad TEXTO/NOTAS ---
  // const handleTextPointerDown = (e) => {
  //     setIsDraggingText(true);
  //     dragTextAnchor.current = {
  //         x: e.clientX || (e.touches && e.touches[0].clientX),
  //         y: e.clientY || (e.touches && e.touches[0].clientY),
  //         startX: textNotesX,
  //         startY: textNotesY
  //     };
  //     if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
  // };
  // const handleTextPointerMove = (e) => {
  //     if (!isDraggingText) return;
  //     const curX = e.clientX || (e.touches && e.touches[0].clientX);
  //     const curY = e.clientY || (e.touches && e.touches[0].clientY);
  //     const deltaX = curX - dragTextAnchor.current.x;
  //     const deltaY = curY - dragTextAnchor.current.y;
      
  //     let newX = Math.round(dragTextAnchor.current.startX - (deltaX * 3));
  //     let newY = Math.round(dragTextAnchor.current.startY - (deltaY * 3));
      
  //     setTextNotesX(Math.max(-500, Math.min(MAX_X_TEXT, newX)));
  //     setTextNotesY(Math.max(-200, Math.min(MAX_Y_TEXT, newY)));
  // };
  // const handleTextPointerUp = () => setIsDraggingText(false);

    // --- Trackpad MICRÓFONO ---
    const handleMicPointerDown = (e) => {
        setIsDraggingMic(true); dragMicAnchor.current = { x: e.clientX || e.touches[0].clientX, y: e.clientY || e.touches[0].clientY, startX: micX, startY: micY };
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    };
    const handleMicPointerMove = (e) => {
        if (!isDraggingMic) return;
        const deltaX = (e.clientX || e.touches[0].clientX) - dragMicAnchor.current.x;
        const deltaY = (e.clientY || e.touches[0].clientY) - dragMicAnchor.current.y;
        setMicX(Math.max(-200, Math.min(MAX_X, Math.round(dragMicAnchor.current.startX - (deltaX * 3)))));
        setMicY(Math.max(-200, Math.min(MAX_Y, Math.round(dragMicAnchor.current.startY - (deltaY * 3)))));
    };
    const handleMicPointerUp = () => setIsDraggingMic(false);

    // --- Trackpad NOTAS ---
    const handleNotesPointerDown = (e) => {
        setIsDraggingNotes(true); dragNotesAnchor.current = { x: e.clientX || e.touches[0].clientX, y: e.clientY || e.touches[0].clientY, startX: notesX, startY: notesY };
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    };
    const handleNotesPointerMove = (e) => {
        if (!isDraggingNotes) return;
        const deltaX = (e.clientX || e.touches[0].clientX) - dragNotesAnchor.current.x;
        const deltaY = (e.clientY || e.touches[0].clientY) - dragNotesAnchor.current.y;
        setNotesX(Math.max(-200, Math.min(MAX_X, Math.round(dragNotesAnchor.current.startX - (deltaX * 3)))));
        setNotesY(Math.max(-200, Math.min(MAX_Y, Math.round(dragNotesAnchor.current.startY - (deltaY * 3)))));
    };
    const handleNotesPointerUp = () => setIsDraggingNotes(false);

    // --- Trackpad TEXTO ---
    const handleTextPointerDown = (e) => {
        setIsDraggingText(true); dragTextAnchor.current = { x: e.clientX || e.touches[0].clientX, y: e.clientY || e.touches[0].clientY, startX: textX, startY: textY };
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    };
    const handleTextPointerMove = (e) => {
        if (!isDraggingText) return;
        const deltaX = (e.clientX || e.touches[0].clientX) - dragTextAnchor.current.x;
        const deltaY = (e.clientY || e.touches[0].clientY) - dragTextAnchor.current.y;
        setTextX(Math.max(-800, Math.min(MAX_X, Math.round(dragTextAnchor.current.startX - (deltaX * 3)))));
        setTextY(Math.max(-200, Math.min(MAX_Y, Math.round(dragTextAnchor.current.startY - (deltaY * 3)))));
    };
    const handleTextPointerUp = () => setIsDraggingText(false);


  // --------------------------------------------------------------------------------
  // 7. COMUNICACIÓN API (CONTROL DE PANTALLA)
  // --------------------------------------------------------------------------------

  const handleToggleVisible = async () => {
    const newValue = !isVisible;
    setIsVisible(newValue); 
    try { 
        await fetch(`${process.env.REACT_APP_API_URL}karaoke/visible?visible=${newValue}`, { method: 'POST' }); 
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: newValue ? 'Letras mostradas en pantalla' : 'Letras ocultas' });
    } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión' }); }
  };

  const handleToggleFilling = async () => {
    const newValue = !isFillingEnabled;
    setIsFillingEnabled(newValue); 
    try { 
        await fetch(`${process.env.REACT_APP_API_URL}karaoke/fill?fill=${newValue}`, { method: 'POST' }); 
        if (activeBlock) { sendCurrentLyricToScreen(activeBlock, newValue); }
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: newValue ? 'Rellenado activado' : 'Rellenado desactivado' });
    } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error' }); }
  };

  const handleSaveColors = async (newColors) => {
    setColorSettings(newColors);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}karaoke/colors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ BaseColor: newColors.base, FillColor: newColors.fill, StrokeColor: newColors.stroke })
      });
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Colores actualizados correctamente' });
    } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al guardar colores' }); }
  };

  const handleResetColors = () => {
      handleSaveColors(DEFAULT_COLORS);
      setColorSettings(DEFAULT_COLORS); 
  };

  const handleDeleteSong = async () => {
    if (!selectedSongId) return;
    if (!window.confirm("¿Seguro que deseas eliminar esta canción?")) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs/${selectedSongId}`, { method: 'DELETE' });
      if (response.ok) {
        setSongs(prev => prev.filter(s => s.id !== selectedSongId));
        setSelectedSongId(""); 
        stopPlayback(); 
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción eliminada' });
      } else { showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo eliminar' }); }
    } catch (error) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de red' }); }
  };

  const sendCurrentLyricToScreen = async (block, forceFilling = null) => {
    if (!block) return;
    const payload = {
      text: block.mode === 'uniform' ? block.text : block.parts?.map(p => p.text).join("") || "",
      mode: block.type === 'instrumental' ? 'instrumental' : 'lyric',
      duration: getBlockDurationMs(block)
    };
    try {
      await fetch(`${process.env.REACT_APP_API_URL}karaoke/lyric`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (e) { console.error("Error enviando lyric:", e); }
  };

  useEffect(() => {
    if (activeBlock) {
      sendCurrentLyricToScreen(activeBlock);
      scrollToActiveLyricInList(); 
    }
  }, [activeBlockIndex]);


  // --------------------------------------------------------------------------------
  // 8. FUNCIONES DE LISTA (SCROLL)
  // --------------------------------------------------------------------------------

  const scrollToActiveLyricInList = () => {
    if (lyricsListRef.current) {
      const listItems = lyricsListRef.current.children;
      const activeItem = listItems[activeBlockIndex];
      if (activeItem) { activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    }
  };

  const scrollListUp = () => { if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: -100, behavior: 'smooth' }); };
  const scrollListDown = () => { if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: 100, behavior: 'smooth' }); };

  // --------------------------------------------------------------------------------
  // 9. CONTROLES DE REPRODUCCIÓN
  // --------------------------------------------------------------------------------

  const playPlayback = () => {
    if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona una canción válida.' });
    setIsPlaying(true);
    if (activeBlock) { sendCurrentLyricToScreen(activeBlock); }
    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reproduciendo...' });
  };

  const pausePlayback = () => {
    setIsPlaying(false);
    lastFrameTimeRef.current = 0;
    showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Pausado' });
  };

  const stopPlayback = () => {
    setIsPlaying(false);
    setActiveBlockIndex(0);
    setElapsedInBlockMs(0);
    lastFrameTimeRef.current = 0;
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
  };

  const goToNext = () => {
    setElapsedInBlockMs(0);
    setActiveBlockIndex(prev => (prev + 1 >= blocks.length ? 0 : prev + 1));
  };

  const goToPrev = () => {
    setElapsedInBlockMs(0);
    setActiveBlockIndex(prev => (prev - 1 < 0 ? 0 : prev - 1));
  };
  
  const goToStart = () => { 
      stopPlayback(); 
      showMessage({ type: MESSAGE_TYPE.INFO, text: 'Regresando al inicio' });
  };
  
  const goToEnd = () => {
    setElapsedInBlockMs(0);
    setActiveBlockIndex(blocks.length - 1);
    showMessage({ type: MESSAGE_TYPE.INFO, text: 'Saltando al final' });
  };

  const handleJumpToLine = (index) => {
      setElapsedInBlockMs(0);
      setActiveBlockIndex(index);
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Saltando a línea #${index + 1}` });
  };

  // --------------------------------------------------------------------------------
  // 10. LOOP DE ANIMACIÓN
  // --------------------------------------------------------------------------------
  useEffect(() => {
    if (!isPlaying || !activeBlock) return;

    const tick = (now) => {
      if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
      const deltaMs = now - lastFrameTimeRef.current;
      lastFrameTimeRef.current = now;

      setElapsedInBlockMs(prev => {
        const duration = getBlockDurationMs(activeBlock);
        const nextElapsed = prev + deltaMs;

        if (duration > 0 && nextElapsed >= duration) {
          const nextIdx = activeBlockIndex + 1;
          if (nextIdx >= blocks.length) {
            setIsPlaying(false); 
            return duration; 
          }
          setActiveBlockIndex(nextIdx); 
          lastFrameTimeRef.current = 0; 
          return 0;
        }
        return nextElapsed;
      });
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
    return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
  }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]);

  const ReturnToDashboard = async () => {
    try {
      await BLResetCurrentDynamic(); 
      getOutOfTheDynamic();
    } catch (error) { }
  }


  // --------------------------------------------------------------------------------
  // 11. RENDERIZADO
  // --------------------------------------------------------------------------------
  return (
    <SecondaryBase actionToExecute={ReturnToDashboard}>
      <div className='Karaoke'>
        
        <p className='Karaoke__Text--Title'><i className='bi bi-file-music'></i> Karaoke</p>

        <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Buttons--Center' onClick={() => navigate('/admin/karaoke/create')}>
          <i className='bi bi-plus-circle'></i> Crear canción
        </button>

        <div className='Karaoke__Container mb-4'>
          
          {/* === PANEL DECK === */}
          <div className='Karaoke__Container__Deck Element--Shadow'>
            
            {/* A. Botones de Configuración */}
            <div className='Karaoke__Container__Deck__SettingButtons'>
              {selectedSongId && (
                <button 
                    type="button" 
                    className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons'
                    onClick={handleDeleteSong}
                    title="Borrar canción"
                >
                  <i className='bi bi-trash'></i> Borrar canción
                </button>
              )}

              {/* Botón Rellenar */}
              <button 
                type='button' 
                className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isFillingEnabled ? 'Btn__Adsl--Red' : ''}`} 
                onClick={handleToggleFilling}
                title={isFillingEnabled ? "Desactivar Relleno" : "Activar Relleno"}
              >
                <i className='bi bi-paint-bucket'></i> {isFillingEnabled ? 'Sin relleno' : 'Rellenar'}
              </button>
              
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => setIsColorModalOpen(true)}>
                <i className='bi bi-palette'></i> Colores
              </button>
              
              {/* Botón Ocultar/Mostrar */}
              <button 
                type='button' 
                className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isVisible ? 'Btn__Adsl--Red' : ''}`} 
                onClick={handleToggleVisible}
                title={isVisible ? "Ocultar en Pantalla" : "Mostrar en Pantalla"}
              >
                <i className={`bi ${isVisible ? 'bi-eye-slash' : 'bi-eye'}`}></i> {isVisible ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>

            {/* B. Playlist + Leyenda */}
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    <select 
                        className='Karaoke__Container__Deck__Playlist' 
                        value={selectedSongId} 
                        onChange={(e) => setSelectedSongId(e.target.value)}
                        style={{ flex: 1 }}
                    >
                        <option value="">-- Selecciona una canción --</option>
                        {songs.map(song => (
                            <option key={song.id} value={song.id}>{song.name} - {song.artist}</option>
                        ))}
                    </select>
                </div>
                <div style={{ textAlign: "center", fontSize: "0.85rem", color: "#666", marginTop: 5, fontWeight: "bold" }}>
                    {statusLegend}
                </div>
            </div>

            {/* C. Preview */}
            <div className='Karaoke__Container__Deck__Screen'>
               <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                 <div style={{ position: "relative", textAlign: "center", fontSize: 24, fontWeight: "900" }}>
                    <div style={{ color: colorSettings.base, WebkitTextStroke: `1px ${colorSettings.stroke}` }}>
                        {displayText || "..."}
                    </div>
                    {/* Visualización local del relleno: obedece al estado isFillingEnabled */}
                    {isFillingEnabled && (
                        <div style={{ 
                            position: "absolute", inset: 0, overflow: "hidden", whiteSpace: "nowrap",
                            width: `${(fillProgress * 100).toFixed(2)}%`,
                            color: colorSettings.fill, 
                            WebkitTextStroke: `1px ${colorSettings.stroke}`
                        }}>
                            {displayText || ""}
                        </div>
                    )}
                 </div>
               </div>
            </div>

            {/* D. Controles Playback */}
            <div className='Karaoke__Container__Deck__ControlButtons'>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToStart} title="Inicio"><i className='bi bi-chevron-bar-left'></i></button>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToPrev} title="Línea Anterior"><i className='bi bi-arrow-left'></i></button>
              
              {!isPlaying ? (
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={playPlayback} title="Reproducir"><i className='bi bi-play-fill'></i></button>
              ) : (
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={pausePlayback} title="Pausar"><i className='bi bi-pause-fill'></i></button>
              )}

              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={stopPlayback} title="Detener"><i className='bi bi-stop-fill'></i></button>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToNext} title="Línea Siguiente"><i className='bi bi-arrow-right'></i></button>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToEnd} title="Fin"><i className='bi bi-chevron-bar-right'></i></button>
            </div>
          </div>

          {/* === PANEL LYRICS === */}
          <div className='Karaoke__Container__Lyrics Element--Shadow'>
            {!selectedSong ? (
               <p style={{textAlign:"center", color:"#999", marginTop: 20}}>Selecciona una canción.</p>
            ) : (
               <>
                 <div style={{textAlign:"center", marginBottom: 5, fontWeight:"bold", fontSize:"0.8rem"}}>
                    Total Líneas: {blocks.length}
                 </div>

                 {/* Botón Scroll Arriba */}
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListUp}>
                    <i className='bi bi-chevron-up'></i>
                 </button>

                 <div className='Karaoke__Container__Lyrics__Box'>
                   <ul className='Karaoke__Container__Lyrics__Box__List' ref={lyricsListRef}>
                     {blocks.map((block, index) => {
                       const text = block.type === 'instrumental' 
                          ? '🎵 (Instrumental)' 
                          : (block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""));
                       
                       const isActive = index === activeBlockIndex;

                       return (
                         <li 
                           key={block.id || index} 
                           className={`Karaoke__Container__Lyrics__Box__List__Items ${isActive ? 'Karaoke__Container__Lyrics__Box__List__Items--Active' : ''}`}
                           onClick={() => handleJumpToLine(index)}
                         >
                           <strong>#{index + 1}</strong> - {text}
                         </li>
                       );
                     })}
                   </ul>
                 </div>

                 {/* Botón Scroll Abajo */}
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListDown}>
                    <i className='bi bi-chevron-down'></i>
                 </button>
               </>
            )}
          </div>
        </div>

        {/* ===================== SECCIÓN EXCLUSIVA: OVERLAY KARAOKE CAM ===================== */}
        <div className="card shadow-sm border-info mt-5" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="card-header bg-info text-white font-weight-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-camera-video-fill me-2"></i> Karaoke Cam Overlay (OBS)</span>
                <span className={`badge ${isCamSyncing ? 'bg-warning text-dark' : 'bg-success'}`}>
                    {isCamSyncing ? 'Sincronizando...' : 'En Vivo'}
                </span>
            </div>
            <div className="card-body">
                
                {/* SWITCHES GENERALES */}
                <div className="d-flex justify-content-center flex-wrap gap-4 mb-4 p-3 bg-light rounded border">
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showMic} onChange={(e) => setShowMic(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎤 Micrófono</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showNotes} onChange={(e) => setShowNotes(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎵 Notas</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🔤 Texto</label>
                    </div>
                    <button className="btn btn-outline-danger btn-sm font-weight-bold" onClick={() => {setShowMic(false); setShowNotes(false); setShowText(false);}}>Ocultar Todo</button>
                </div>

                <div className="row g-4">
                    {/* --- COLUMNA 1: MICRÓFONO --- */}
                    <div className="col-md-4 border-end">
                        <h6 className="font-weight-bold text-primary border-bottom pb-2 mb-3 text-center">🎤 Micrófono</h6>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Rotación:</span><span>{micRotation}°</span></label><input type="range" className="form-range" min="-90" max="90" step="5" value={micRotation} onChange={(e) => setMicRotation(parseInt(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Escala:</span><span>{micScale}x</span></label><input type="range" className="form-range" min="0.3" max="2.5" step="0.1" value={micScale} onChange={(e) => setMicScale(parseFloat(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span><span>{micX}px</span></label><input type="range" className="form-range" min="-200" max={MAX_X} step="10" value={micX} onChange={(e) => setMicX(parseInt(e.target.value))} /></div>
                        <div className="mb-3"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span><span>{micY}px</span></label><input type="range" className="form-range" min="-200" max={MAX_Y} step="10" value={micY} onChange={(e) => setMicY(parseInt(e.target.value))} /></div>
                        
                        <div className="d-flex justify-content-center">
                            <div style={{ width: '100%', maxWidth: '250px', height: '140px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #0d6efd', touchAction: 'none', cursor: isDraggingMic ? 'grabbing' : 'grab' }} onPointerDown={handleMicPointerDown} onPointerMove={handleMicPointerMove} onPointerUp={handleMicPointerUp} onPointerCancel={handleMicPointerUp} onPointerLeave={handleMicPointerUp}>
                                <div style={{ position: 'absolute', right: `${(micX / MAX_X) * 90}%`, bottom: `${(micY / MAX_Y) * 80}%`, width: '10%', height: '30%', backgroundColor: isDraggingMic ? '#0d6efd' : 'rgba(13, 110, 253, 0.5)', borderRadius: '10px', transform: `scale(${micScale}) rotate(${micRotation}deg)`, transformOrigin: 'bottom center', pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                    </div>

                    {/* --- COLUMNA 2: NOTAS --- */}
                    <div className="col-md-4 border-end">
                        <h6 className="font-weight-bold text-success border-bottom pb-2 mb-3 text-center">🎵 Notas Musicales</h6>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Escala:</span><span>{notesScale}x</span></label><input type="range" className="form-range" min="0.3" max="2.5" step="0.1" value={notesScale} onChange={(e) => setNotesScale(parseFloat(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span><span>{notesX}px</span></label><input type="range" className="form-range" min="-200" max={MAX_X} step="10" value={notesX} onChange={(e) => setNotesX(parseInt(e.target.value))} /></div>
                        <div className="mb-3 mt-4"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span><span>{notesY}px</span></label><input type="range" className="form-range" min="-200" max={MAX_Y} step="10" value={notesY} onChange={(e) => setNotesY(parseInt(e.target.value))} /></div>
                        
                        <div className="d-flex justify-content-center mt-5">
                            <div style={{ width: '100%', maxWidth: '250px', height: '140px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #198754', touchAction: 'none', cursor: isDraggingNotes ? 'grabbing' : 'grab' }} onPointerDown={handleNotesPointerDown} onPointerMove={handleNotesPointerMove} onPointerUp={handleNotesPointerUp} onPointerCancel={handleNotesPointerUp} onPointerLeave={handleNotesPointerUp}>
                                <div style={{ position: 'absolute', right: `${(notesX / MAX_X) * 90}%`, bottom: `${(notesY / MAX_Y) * 80}%`, width: '20px', height: '20px', backgroundColor: isDraggingNotes ? '#198754' : 'rgba(25, 135, 84, 0.5)', borderRadius: '50%', transform: `scale(${notesScale})`, pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                    </div>

                    {/* --- COLUMNA 3: TEXTO DINÁMICO --- */}
                    <div className="col-md-4">
                        <h6 className="font-weight-bold text-danger border-bottom pb-2 mb-3 text-center">🔤 Texto</h6>
                        <div className="mb-3">
                            <label className="small font-weight-bold">Contenido del Texto:</label>
                            <input type="text" className="form-control form-control-sm" value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="Ej: ¡Baila con nosotros!" />
                        </div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Escala:</span><span>{textScale}x</span></label><input type="range" className="form-range" min="0.3" max="2.5" step="0.1" value={textScale} onChange={(e) => setTextScale(parseFloat(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span><span>{textX}px</span></label><input type="range" className="form-range" min="-800" max={MAX_X} step="10" value={textX} onChange={(e) => setTextX(parseInt(e.target.value))} /></div>
                        <div className="mb-3"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span><span>{textY}px</span></label><input type="range" className="form-range" min="-200" max={MAX_Y} step="10" value={textY} onChange={(e) => setTextY(parseInt(e.target.value))} /></div>

                        <div className="d-flex justify-content-center">
                            <div style={{ width: '100%', maxWidth: '250px', height: '140px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #dc3545', touchAction: 'none', cursor: isDraggingText ? 'grabbing' : 'grab' }} onPointerDown={handleTextPointerDown} onPointerMove={handleTextPointerMove} onPointerUp={handleTextPointerUp} onPointerCancel={handleTextPointerUp} onPointerLeave={handleTextPointerUp}>
                                <div style={{ position: 'absolute', right: `${(textX / MAX_X) * 80}%`, bottom: `${(textY / MAX_Y) * 80}%`, padding: '2px 8px', border: '1px solid #dc3545', backgroundColor: isDraggingText ? '#dc3545' : 'rgba(220, 53, 69, 0.5)', color: 'white', fontSize: '0.5rem', transform: `scale(${textScale})`, pointerEvents: 'none' }}>TEXTO</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        {/* ============================================================================================== */}
        {/* NUEVA SECCIÓN: CONTROLES DE LA KARAOKE CAM (OVERLAY) */}
        {/* ============================================================================================== */}
        {/* <div className="card shadow-sm border-info mt-5" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="card-header bg-info text-white font-weight-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-camera-video-fill me-2"></i> Karaoke Cam Overlay (OBS)</span>
                <span className={`badge ${isCamSyncing ? 'bg-warning text-dark' : 'bg-success'}`}>
                    {isCamSyncing ? 'Sincronizando...' : 'En Vivo'}
                </span>
            </div>
            <div className="card-body">
                
                {/* --- SWITCHES DE VISIBILIDAD (Arriba al centro) --- /}
                <div className="d-flex justify-content-center flex-wrap gap-4 mb-4 p-3 bg-light rounded border">
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showMic} onChange={(e) => setShowMic(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎤 Mostrar Micrófono</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showNotes} onChange={(e) => setShowNotes(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎵 Mostrar Notas</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🔤 Mostrar Texto</label>
                    </div>
                    <button className="btn btn-outline-danger btn-sm font-weight-bold" onClick={() => {setShowMic(false); setShowNotes(false); setShowText(false);}}>Ocultar Todo</button>
                </div>

                <div className="row g-4">
                    {/* --- COLUMNA 1: CONTROLES DEL MICRÓFONO --- /}
                    <div className="col-md-6 border-end">
                        <h6 className="font-weight-bold text-primary border-bottom pb-2 mb-3 text-center">Física del Micrófono</h6>
                        
                        <div className="mb-3">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Rotación (Inclinación):</span> <span className="text-primary">{micRotation}°</span></label>
                            <input type="range" className="form-range" min="-90" max="90" step="5" value={micRotation} onChange={(e) => setMicRotation(parseInt(e.target.value))} />
                            <div className="d-flex justify-content-between small text-muted"><span>◀ Izquierda</span><span>Derecha ▶</span></div>
                        </div>

                        <div className="mb-2">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span> <span className="text-primary">{micX}px</span></label>
                            <input type="range" className="form-range" min="-200" max={MAX_X_MIC} step="10" value={micX} onChange={(e) => setMicX(parseInt(e.target.value))} />
                        </div>

                        <div className="mb-4">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span> <span className="text-primary">{micY}px</span></label>
                            <input type="range" className="form-range" min="-200" max={MAX_Y_MIC} step="10" value={micY} onChange={(e) => setMicY(parseInt(e.target.value))} />
                        </div>

                        {/* Trackpad del Micrófono /}
                        <div className="d-flex justify-content-center">
                            <div 
                                style={{ width: '100%', maxWidth: '300px', height: '168px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #0d6efd', touchAction: 'none', cursor: isDraggingMic ? 'grabbing' : 'grab' }}
                                onPointerDown={handleMicPointerDown} onPointerMove={handleMicPointerMove} onPointerUp={handleMicPointerUp} onPointerCancel={handleMicPointerUp} onPointerLeave={handleMicPointerUp}
                            >
                                <div style={{
                                    position: 'absolute', right: `${(micX / MAX_X_MIC) * 90}%`, bottom: `${(micY / MAX_Y_MIC) * 80}%`,
                                    width: '10%', height: '30%', backgroundColor: isDraggingMic ? '#0d6efd' : 'rgba(13, 110, 253, 0.5)', borderRadius: '10px',
                                    transform: `rotate(${micRotation}deg)`, transformOrigin: 'bottom center', pointerEvents: 'none', transition: isDraggingMic ? 'none' : 'all 0.1s ease-out'
                                }}></div>
                                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed #333', pointerEvents: 'none' }}></div>
                                <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, borderLeft: '1px dashed #333', pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                        <p className="text-center small text-muted mt-2">Trackpad Micrófono</p>
                    </div>

                    {/* --- COLUMNA 2: CONTROLES DE TEXTO/NOTAS --- /}
                    <div className="col-md-6">
                        <h6 className="font-weight-bold text-success border-bottom pb-2 mb-3 text-center">Física de Texto y Notas</h6>
                        
                        <div className="mb-2 mt-4 pt-3">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span> <span className="text-success">{textNotesX}px</span></label>
                            <input type="range" className="form-range" min="-500" max={MAX_X_TEXT} step="10" value={textNotesX} onChange={(e) => setTextNotesX(parseInt(e.target.value))} />
                        </div>

                        <div className="mb-4 pt-2">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span> <span className="text-success">{textNotesY}px</span></label>
                            <input type="range" className="form-range" min="-200" max={MAX_Y_TEXT} step="10" value={textNotesY} onChange={(e) => setTextNotesY(parseInt(e.target.value))} />
                        </div>

                        {/* Trackpad de Texto/Notas /}
                        <div className="d-flex justify-content-center mt-5">
                            <div 
                                style={{ width: '100%', maxWidth: '300px', height: '168px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #198754', touchAction: 'none', cursor: isDraggingText ? 'grabbing' : 'grab' }}
                                onPointerDown={handleTextPointerDown} onPointerMove={handleTextPointerMove} onPointerUp={handleTextPointerUp} onPointerCancel={handleTextPointerUp} onPointerLeave={handleTextPointerUp}
                            >
                                <div style={{
                                    position: 'absolute', right: `${(textNotesX / MAX_X_TEXT) * 80}%`, bottom: `${(textNotesY / MAX_Y_TEXT) * 80}%`,
                                    padding: '5px 15px', border: '2px solid #198754', backgroundColor: isDraggingText ? '#198754' : 'rgba(25, 135, 84, 0.5)', borderRadius: '4px',
                                    color: 'white', fontSize: '0.6rem', fontWeight: 'bold', pointerEvents: 'none', transition: isDraggingText ? 'none' : 'all 0.1s ease-out'
                                }}>
                                    TEXTO / NOTAS
                                </div>
                                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed #333', pointerEvents: 'none' }}></div>
                                <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, borderLeft: '1px dashed #333', pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                        <p className="text-center small text-muted mt-2">Trackpad Texto y Notas</p>

                    </div>
                </div>
            </div>
        </div> */}

        {/* Modal Colores */}
        <ColorSettingsModal 
          isOpen={isColorModalOpen}
          onClose={() => setIsColorModalOpen(false)}
          currentSettings={colorSettings}
          onSave={handleSaveColors}
          onReset={handleResetColors}
        />

      </div>
    </SecondaryBase>
  );
};

export { Karaoke };